import React, { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import { useLocation, useNavigate } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import { useCrmPeopleStore, PAGE_SIZE } from '../stores/crmPeopleStore'
import { useOpenPerson } from '../hooks/useOpenPerson'
import { useIsMobile } from '../hooks/useIsMobile'
import { usePageTour } from '../hooks/usePageTour'
import { CrmTopbarPortal, useCrmOwner } from '../CrmLayout'
import { ownerColor } from '../PipelinePage/OpportunityCard'
import { Pagination } from '../../../components/Pagination'
import { ConfirmDialog } from '../PersonDetailPage/ConfirmDialog'
import { HouseholdModal } from '../HouseholdModal'
import './PeoplePage.scss'

const money = (v) => {
    const n = Number(v)
    if (!v || Number.isNaN(n)) return '—'
    return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}

const monthYear = (iso) => {
    if (!iso) return '—'
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

// Inception dates arrive date-only ("2025-01-15"). Split rather than parse:
// new Date() reads that form as UTC and renders it in local time, which shows
// the previous day for anyone west of Greenwich.
const mdY = (value) => {
    if (!value) return '—'
    const [year, month, day] = String(value).split('-')
    return year && month && day ? `${month}/${day}/${year}` : '—'
}

// Whole months from now until the given date (null if past/absent).
const monthsUntil = (iso) => {
    if (!iso) return null
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    const now = new Date()
    const m = (d.getFullYear() - now.getFullYear()) * 12 + (d.getMonth() - now.getMonth())
    return m
}

const initials = (name) => {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/)
    return (parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)).toUpperCase()
}

// A household's visible members: first names on the desktop row, a count on phones.
const firstNames = (h) => h.members.map((m) => (m.name || 'Unnamed').trim().split(/\s+/)[0]).join(', ')
const memberSummary = (h) => `${h.member_count} ${h.member_count === 1 ? 'member' : 'members'}`

const STAGE_COLOR = { LEAD: '#3578c8', QUOTE: '#7048cc', APPLICATION: '#d07020', SUBMISSION: '#d07020', SCHEDULED: '#2e9967' }
const TIER_COLOR = { HOT: '#c93535', WARM: '#d07020', COLD: '#3578c8' }
const cap = (s) => (s ? s.charAt(0) + s.slice(1).toLowerCase() : '')
const pill = (label, color) => (
    <span className="status-pill" style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}>{label}</span>
)

const RepTag = ({ owner }) => (
    <span className="rep-tag">
        <span className="rep-mini" style={{ background: ownerColor(owner?.id) }}>{initials(owner?.name)}</span>
        {owner?.name || '—'}
    </span>
)

// Segments in the order the toggle presents them; Clients is the default.
const SEGMENTS = [
    { key: 'clients', label: 'Clients' },
    { key: 'leads', label: 'Current Leads' },
    { key: 'cold', label: 'Dead Leads' },
    { key: 'households', label: 'Households' },
]

// Each table's headers, in order. A column is sortable exactly when it names a
// `sortKey` — the key the backend whitelists for that segment — so a column
// that shouldn't reorder anything simply doesn't have one. Deliberately absent
// here: Phone and Email (never asked for), Current Vehicle and the inception
// dates stacked inside it (a row can list several cars, so there is no one
// value to sort on), Status (two states), Last Profit (a summed property), and
// Action (a button).
const COLUMNS = {
    clients: [
        { label: 'Client', sortKey: 'name' },
        { label: 'Phone' },
        { label: 'Email', className: 'email' },
        { label: 'Salesperson', sortKey: 'salesperson' },
        { label: 'Current Vehicle' },
        { label: 'Expires', sortKey: 'expires' },
        { label: 'Status' },
        { label: 'Last Profit', className: 'r' },
    ],
    leads: [
        { label: 'Name', sortKey: 'name' },
        { label: 'Phone' },
        { label: 'Email', className: 'email' },
        { label: 'Salesperson', sortKey: 'salesperson' },
        { label: 'Stage', sortKey: 'stage' },
        { label: 'Tier', sortKey: 'tier' },
        { label: 'Source', sortKey: 'source' },
        { label: 'Added', sortKey: 'added' },
    ],
    cold: [
        { label: 'Contact', sortKey: 'name' },
        { label: 'Salesperson', sortKey: 'salesperson' },
        { label: 'Added', sortKey: 'added' },
        { label: 'Action', className: 'r' },
    ],
    households: [
        { label: 'Household' },
        { label: 'Members', className: 'r' },
    ],
}

// A sortable header is a real button so it answers to click, Tab and Enter or
// Space alike; a non-sortable one is a bare <th> with nothing to focus, no
// aria-sort and nothing for the stylesheet to make look clickable.
const HeadCell = ({ col, sort, onSort }) => {
    if (!col.sortKey) return <th className={col.className}>{col.label}</th>

    const active = sort?.key === col.sortKey
    const Arrow = sort?.dir === 'desc' ? ArrowDownwardIcon : ArrowUpwardIcon
    return (
        <th
            className={`sortable${active ? ' sorted' : ''}${col.className ? ` ${col.className}` : ''}`}
            aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
            <button type="button" onClick={() => onSort(col.sortKey)}>
                {col.label}
                {/* Only the sorted column carries an arrow — a placeholder on
                    every other header is noise in a row this small. */}
                {active && <Arrow className="sort-arrow" />}
            </button>
        </th>
    )
}

const SEARCH_DEBOUNCE_MS = 300

// The People tour (see usePageTour). Canonical order; a step whose target isn't
// on screen (an empty tab, a member, Select mode) is left out of a run.
const PEOPLE_TOUR_VERSION = 1
const PEOPLE_TOUR_STEPS = [
    {
        element: '.people-page .subtabs',
        data: { id: 'segments' },
        popover: {
            title: 'Your people',
            description: 'Clients have bought from you, Current Leads have an open opportunity, and Dead Leads have neither. Households group a family or business.',
        },
    },
    {
        // A table row on desktop, a card on phones.
        element: '.people-page [data-person-id]',
        data: { id: 'person' },
        popover: {
            title: 'Open a person',
            description: 'Select a person to view their details.',
        },
    },
    {
        // In the tab bar on desktop, the page's own toolbar on phones.
        element: '.crm-page-actions .search-box, .people-page .search-box',
        data: { id: 'search' },
        popover: {
            title: 'Find someone',
            description: 'Search this list by name.',
        },
    },
    {
        element: '.crm-view-select',
        data: { id: 'user-filter' },
        popover: {
            title: 'Choose whose people to view',
            description: 'View everyone’s people together or focus on a specific team member.',
        },
    },
    {
        element: '.people-page .btn-select',
        data: { id: 'select' },
        popover: {
            title: 'Work with several at once',
            description: 'Use Select to pick several people, then delete them or mark current leads as dead.',
        },
    },
    {
        element: '.people-page .crm-tour-btn',
        data: { id: 'replay' },
        popover: {
            title: 'Replay any time',
            description: 'You can start this tutorial again anytime from here.',
        },
    },
]

export const PeoplePage = () => {
    const openPerson = useOpenPerson()
    const navigate = useNavigate()
    const location = useLocation()
    const isMobile = useIsMobile()
    // Back from a household returns here with `state.tab`.
    const [tab, setTab] = useState(location.state?.tab === 'households' ? 'households' : 'clients')
    const [search, setSearch] = useState('')
    // Select mode. Person ids in a Set: one pick per person however many lead
    // sub-rows they have, and picks survive paging, which replaces the rows.
    const [selecting, setSelecting] = useState(false)
    const [selectedIds, setSelectedIds] = useState(() => new Set())
    const [confirmAction, setConfirmAction] = useState(null)
    const [busy, setBusy] = useState(false)
    const [creatingHousehold, setCreatingHousehold] = useState(false)

    const clearSelection = () => setSelectedIds(new Set())
    const exitSelect = () => { setSelecting(false); clearSelection() }
    const toggleSelected = (id) => setSelectedIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
    })

    const { clients, leads, cold, households, fetchAll, fetchSegment, fetchStages, promote, setSort, bulkDelete, bulkMarkDead } = useCrmPeopleStore(
        useShallow((state) => ({
            clients: state.clients,
            leads: state.leads,
            cold: state.cold,
            households: state.households,
            fetchAll: state.fetchAll,
            fetchSegment: state.fetchSegment,
            fetchStages: state.fetchStages,
            promote: state.promote,
            setSort: state.setSort,
            bulkDelete: state.bulkDelete,
            bulkMarkDead: state.bulkMarkDead,
        }))
    )

    const bodyRef = useRef(null)

    // Nearest ancestor that actually overflows, or null when the document is the
    // one scrolling. Resolved at runtime rather than assumed: .people-body is
    // declared overflow:auto, but the CRM shell's height:100% chain has no
    // definite height to resolve against (nothing above #HomePage sets one), so
    // it grows to fit its rows and never gets a scrollbar — the document scrolls
    // instead. Reading it live means this keeps working either way.
    const scrollOwnerOf = (node) => {
        for (let el = node; el && el !== document.body; el = el.parentElement) {
            const { overflowY } = window.getComputedStyle(el)
            if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
                return el
            }
        }
        return null
    }

    const showResultsFromTop = () => {
        const list = bodyRef.current
        if (!list) return
        const owner = scrollOwnerOf(list)
        if (owner) {
            owner.scrollTop = 0
        } else {
            // The whole page scrolls as one here, so its top *is* the top of the
            // results — and it keeps the toolbar and segment tabs in view.
            window.scrollTo(0, 0)
        }
    }

    useEffect(() => {
        fetchStages()
    }, [])

    // One page per segment on mount, for the topbar view's owner, and again
    // whenever the search term settles. All three load so the tab badges are
    // populated whichever tab is open.
    const owner = useCrmOwner()
    const firstRun = useRef(true)
    useEffect(() => {
        if (firstRun.current) {
            firstRun.current = false
            fetchAll('', owner)
            return
        }
        const timer = setTimeout(() => {
            // A new term is a new result set from page 1, so the old offset
            // would drop the user into the middle of it.
            showResultsFromTop()
            // New results: picks from the old set would act on people no
            // longer shown.
            clearSelection()
            fetchAll(search)
        }, SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [search])

    // A new view is a new result set too, at once: page 1 for its owner, from
    // the top, nothing picked. The typed search still applies.
    const ownerRun = useRef(true)
    useEffect(() => {
        if (ownerRun.current) {
            ownerRun.current = false
            return
        }
        showResultsFromTop()
        clearSelection()
        fetchAll(search, owner)
    }, [owner])

    // The first-visit tour, once all three segments have loaded (their badges
    // resize the tabs) and none failed. Read live — a warm store's value at
    // mount predates the fetch the effect above has just started.
    const tour = usePageTour({ key: 'people', version: PEOPLE_TOUR_VERSION, steps: PEOPLE_TOUR_STEPS })
    useEffect(function autoStartTour() {
        const s = useCrmPeopleStore.getState()
        if (selecting || [s.clients, s.leads, s.cold].some((seg) => seg.loading || seg.error)) return
        tour.autoStart()
    }, [clients.loading, leads.loading, cold.loading])

    const segments = { clients, leads, cold, households }
    const active = segments[tab]
    const loading = active.loading
    // Badges show each segment's full size, not the filtered page — the same
    // thing they showed when the whole list lived in the browser.
    const counts = { clients: clients.total, leads: leads.total, cold: cold.total, households: households.total }
    const searchNoun = { clients: 'clients', leads: 'leads', cold: 'contacts', households: 'households' }[tab]
    const householdRows = households.rows
    // This entry is re-marked as the Households tab first, so the browser's
    // Back (and the page's) land on it rather than on Clients.
    const openHousehold = (id) => {
        navigate('/crm/people', { replace: true, state: { tab: 'households' } })
        navigate(`/crm/people/households/${id}`)
    }

    // After a household was created, whether members were added or skipped. It
    // is newest, so page 1 shows it; a search that would hide it is cleared,
    // which refetches every tab (and every count) anyway.
    const onHouseholdDone = () => {
        setCreatingHousehold(false)
        showResultsFromTop()
        if (search) setSearch('')
        else fetchSegment('households', { page: 1 })
    }

    const clientRows = clients.rows
    // Each lead row is one person; the backend groups them and sends every open
    // opportunity it has, so `opportunities.length` is the "N open" badge and the
    // sub-rows come from the same collection.
    const leadRows = leads.rows
    const coldRows = cold.rows

    // "Select all" is this page only; other pages' picks are never touched.
    const pageIds = active.rows.map((row) => row.id)
    const onPage = pageIds.filter((id) => selectedIds.has(id)).length
    const allOnPage = pageIds.length > 0 && onPage === pageIds.length
    const togglePage = () => setSelectedIds((prev) => {
        const next = new Set(prev)
        pageIds.forEach((id) => (allOnPage ? next.delete(id) : next.add(id)))
        return next
    })

    const rowClick = (id) => (selecting ? toggleSelected(id) : openPerson(id))
    const isPicked = (id) => selecting && selectedIds.has(id)
    const rowClass = (id, extra) => [extra, isPicked(id) && 'selected'].filter(Boolean).join(' ') || undefined
    const cardClass = (id) => `pcard${isPicked(id) ? ' selected' : ''}`
    const span = (key) => COLUMNS[key].length + (selecting ? 1 : 0)

    const checkbox = (person, className) => (
        <Checkbox
            className={className}
            size="small"
            checked={selectedIds.has(person.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleSelected(person.id)}
            inputProps={{ 'aria-label': `Select ${person.name || 'Unnamed'}` }}
        />
    )
    // Lead sub-rows get an empty cell: the checkbox belongs to the person.
    const selectCell = (person, withBox = true) => selecting && (
        <td className="select-col">{withBox && checkbox(person)}</td>
    )
    const avatar = (person, ownerId) => (selecting
        ? checkbox(person, 'pcard-check')
        : <span className="pcard-av" style={{ background: ownerColor(ownerId) }}>{initials(person.name)}</span>)

    const selectedCount = selectedIds.size
    const peopleNoun = (n) => `${n} ${n === 1 ? 'person' : 'people'}`
    const confirmDelete = () => setConfirmAction({
        title: `Delete ${peopleNoun(selectedCount)}?`,
        message: `This permanently deletes ${peopleNoun(selectedCount)}, including all associated `
            + 'opportunities, deal history, activity, and vehicles. Historical reports may change. '
            + 'This cannot be undone.',
        confirmLabel: 'Delete',
        run: bulkDelete,
    })
    const confirmMarkDead = () => setConfirmAction({
        title: `Mark ${peopleNoun(selectedCount)} as dead?`,
        message: 'Every open opportunity for the selected people will be marked as Lost. Won deals '
            + 'are not affected, and a lost opportunity can be reopened from the person’s page.',
        confirmLabel: 'Mark as Dead',
        run: bulkMarkDead,
    })
    // Success ends Select mode. A 400 naming unavailable people drops just those
    // and stays, so the user can review and retry.
    const runConfirmed = async () => {
        const { run } = confirmAction
        setConfirmAction(null)
        setBusy(true)
        const result = await run([...selectedIds], search)
        setBusy(false)
        if (result.ok) {
            exitSelect()
        } else if (result.missing.length) {
            setSelectedIds((prev) => {
                const next = new Set(prev)
                result.missing.forEach((id) => next.delete(id))
                return next
            })
        }
    }

    // Never on Households: the bulk actions delete or kill *people*.
    const bulkBar = selecting && tab !== 'households' && (
        <div className={`bulk-bar${isMobile ? ' bulk-bar-bottom' : ''}`} role="toolbar" aria-label="Bulk actions">
            <span className="bulk-count" aria-live="polite">{selectedCount} selected</span>
            {/* The cards have no header row, so the page toggle lives here. */}
            {isMobile && (
                <button type="button" className="bulk-link" disabled={!pageIds.length} onClick={togglePage}>
                    {allOnPage ? 'Deselect page' : 'Select page'}
                </button>
            )}
            <div className="bulk-spacer" />
            {tab === 'leads' && (
                <button type="button" className="bulk-btn" disabled={!selectedCount || busy} onClick={confirmMarkDead}>
                    Mark as Dead
                </button>
            )}
            <button type="button" className="bulk-btn bulk-danger" disabled={!selectedCount || busy} onClick={confirmDelete}>
                Delete
            </button>
            <button type="button" className="bulk-btn" onClick={exitSelect}>Cancel</button>
        </div>
    )

    // Scrolled synchronously from the click rather than from an effect on
    // `page`: it fires exactly once per deliberate change, can't be re-run by a
    // loading flag or a store update, and the list is already at the top by the
    // time the new rows land.
    const goToPage = (page) => {
        showResultsFromTop()
        return fetchSegment(tab, { page, search })
    }
    const pageCount = Math.max(1, Math.ceil((active.count || 0) / PAGE_SIZE))

    // Ascending, then descending, then back to the segment's own default —
    // each segment has a meaningful one (newest pursuit, newest contact), so a
    // third click restores it rather than cycling through ascending again.
    // Only this segment's sort changes; the other two keep theirs.
    const toggleSort = (key) => {
        const current = active.sort
        const next = current?.key !== key
            ? { key, dir: 'asc' }
            : current.dir === 'asc' ? { key, dir: 'desc' } : null
        showResultsFromTop()
        return setSort(tab, next, search)
    }

    const headers = (key) => (
        <thead>
            <tr>
                {selecting && (
                    <th className="select-col">
                        <Checkbox
                            size="small"
                            checked={allOnPage}
                            indeterminate={onPage > 0 && !allOnPage}
                            disabled={!pageIds.length}
                            onChange={togglePage}
                            inputProps={{ 'aria-label': 'Select all on this page' }}
                        />
                    </th>
                )}
                {COLUMNS[key].map((col) => (
                    <HeadCell key={col.label} col={col} sort={segments[key].sort} onSort={toggleSort} />
                ))}
            </tr>
        </thead>
    )

    const pager = (
        <Pagination
            page={active.page}
            pageCount={pageCount}
            onPageChange={goToPage}
            label={`${searchNoun} pages`}
        />
    )

    return (
        <Box className="people-page">
            <div className="toolbar">
                <span className="toolbar-title">People</span>
                <div className="subtabs">
                    {SEGMENTS.map((s) => (
                        <span
                            key={s.key}
                            className={`subtab${tab === s.key ? ' active' : ''}`}
                            onClick={() => {
                                // Households leave Select mode entirely; it only acts on people.
                                if (s.key === 'households') exitSelect()
                                else if (s.key !== tab) clearSelection()
                                setTab(s.key)
                                showResultsFromTop()
                            }}
                        >
                            {s.label} <span className="rep-count">{counts[s.key]}</span>
                        </span>
                    ))}
                </div>
                <div className="toolbar-divider" />
                {/* In the CRM tab bar; stays in this row on phones. */}
                <CrmTopbarPortal>
                    <div className="search-box">
                        <SearchIcon />
                        <input
                            placeholder={`Search ${searchNoun}…`}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </CrmTopbarPortal>
                {!selecting && (
                    <>
                        <button
                            type="button"
                            className="crm-tour-btn"
                            aria-label="Show Tutorial"
                            title="Show Tutorial"
                            onClick={tour.replay}
                        >
                            <HelpOutlineIcon />
                            <span className="crm-tour-label">Show Tutorial</span>
                        </button>
                        {tab === 'households' ? (
                            <button type="button" className="btn-create" onClick={() => setCreatingHousehold(true)}>
                                <AddIcon /> Create new
                            </button>
                        ) : (
                            <button type="button" className="btn-select" onClick={() => setSelecting(true)}>Select</button>
                        )}
                    </>
                )}
            </div>
            {!isMobile && bulkBar}

            <div className="people-body" ref={bodyRef}>
                {isMobile ? (
                    <>
                    <div className="pcards">
                        {tab === 'clients' && (
                            <>
                                {clientRows.map((c) => (
                                    <div key={c.id} className={cardClass(c.id)} data-person-id={c.id} onClick={() => rowClick(c.id)}>
                                        {avatar(c, c.owner_detail?.id)}
                                        <div className="pcard-main">
                                            <div className="pcard-name">{c.name || 'Unnamed'}</div>
                                            <div className="pcard-sub">{c.current_vehicle || c.phone_number || '—'}</div>
                                        </div>
                                        <div className="pcard-right">
                                            <span className="profit">{money(c.last_profit)}</span>
                                            {c.renewal_due && <span className="status-pill st-renewal">Renewal</span>}
                                        </div>
                                    </div>
                                ))}
                                {clientRows.length === 0 && <div className="empty">{loading ? 'Loading…' : 'No clients yet.'}</div>}
                            </>
                        )}

                        {tab === 'leads' && (
                            <>
                                {leadRows.map((p) => (
                                    <div key={p.id} className={cardClass(p.id)} data-person-id={p.id} onClick={() => rowClick(p.id)}>
                                        {avatar(p, p.opportunities[0]?.owner_detail?.id)}
                                        <div className="pcard-main">
                                            <div className="pcard-name">
                                                {p.name || 'Unnamed'}
                                                {p.opportunities.length > 1 && <span className="multi-badge">{p.opportunities.length} open</span>}
                                            </div>
                                            {p.opportunities.length === 1 ? (
                                                <div className="pcard-sub">{[cap(p.opportunities[0].tier), cap(p.opportunities[0].source)].filter(Boolean).join(' · ') || '—'}</div>
                                            ) : (
                                                <div className="pcard-opps">
                                                    {p.opportunities.map((o) => (
                                                        <div key={o.id} className="pcard-opp">
                                                            {o.stage_detail
                                                                ? pill(o.stage_detail.name, STAGE_COLOR[o.stage_detail.name?.toUpperCase()] || '#888')
                                                                : <span className="pcard-opp-none">No stage</span>}
                                                            <span className="pcard-opp-meta">{[cap(o.tier), cap(o.source)].filter(Boolean).join(' · ')}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        {p.opportunities.length === 1 && (
                                            <div className="pcard-right">
                                                {p.opportunities[0].stage_detail ? pill(p.opportunities[0].stage_detail.name, STAGE_COLOR[p.opportunities[0].stage_detail.name?.toUpperCase()] || '#888') : null}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {leadRows.length === 0 && <div className="empty">{loading ? 'Loading…' : 'No active leads.'}</div>}
                            </>
                        )}

                        {tab === 'cold' && (
                            <>
                                {coldRows.map((p) => (
                                    <div key={p.id} className={cardClass(p.id)} data-person-id={p.id} onClick={() => rowClick(p.id)}>
                                        {avatar(p, p.owner_detail?.id)}
                                        <div className="pcard-main">
                                            <div className="pcard-name">{p.name || 'Unnamed'}</div>
                                            <div className="pcard-sub">{p.phone_number || '—'}</div>
                                        </div>
                                        {!selecting && (
                                            <div className="pcard-right">
                                                <button className="btn-make-lead" onClick={(e) => { e.stopPropagation(); promote(p.id, search) }}>
                                                    <AddIcon style={{ fontSize: 13 }} /> Lead
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {coldRows.length === 0 && <div className="empty">{loading ? 'Loading…' : 'No contacts.'}</div>}
                            </>
                        )}

                        {tab === 'households' && (
                            <>
                                {householdRows.map((h) => (
                                    <div key={h.id} className="pcard" data-household-id={h.id} onClick={() => openHousehold(h.id)}>
                                        <span className="pcard-av hh-av"><HomeOutlinedIcon /></span>
                                        <div className="pcard-main">
                                            <div className="pcard-name">{h.name}</div>
                                            <div className="pcard-sub">{memberSummary(h)}</div>
                                        </div>
                                    </div>
                                ))}
                                {householdRows.length === 0 && <div className="empty">{loading ? 'Loading…' : 'No households yet.'}</div>}
                            </>
                        )}
                    </div>
                    {pager}
                    </>
                ) : (
                <>
                <div className="panel">
                    {tab === 'clients' && (
                        <table>
                            {headers('clients')}
                            <tbody>
                                {clientRows.map((c) => {
                                    const m = monthsUntil(c.expires)
                                    const soon = m != null && m >= 0 && m <= 6
                                    return (
                                        <tr key={c.id} className={rowClass(c.id)} data-person-id={c.id} onClick={() => rowClick(c.id)}>
                                            {selectCell(c)}
                                            <td><div className="cust">{c.name || 'Unnamed'}</div></td>
                                            <td className="phone">{c.phone_number || '—'}</td>
                                            <td className="email"><div className="ellip">{c.email || '—'}</div></td>
                                            <td><RepTag owner={c.owner_detail} /></td>
                                            <td>
                                                {c.current_vehicles?.length
                                                    ? c.current_vehicles.map((v, i) => (
                                                        <div key={i} className="veh-entry">
                                                            {/* Imported history rarely carries a vehicle name, but the
                                                                car and the day they got it are still real. */}
                                                            <div>{v.label || 'Unnamed Vehicle'}</div>
                                                            <div className="veh">Inception: {mdY(v.inception_date)}</div>
                                                        </div>
                                                    ))
                                                    : '—'}
                                            </td>
                                            <td>{monthYear(c.expires)}{soon && <span className="veh"> ({m}mo)</span>}</td>
                                            <td>
                                                {c.renewal_due
                                                    ? <span className="status-pill st-renewal">Renewal due</span>
                                                    : <span className="status-pill st-client">Client</span>}
                                            </td>
                                            <td className="r profit">{money(c.last_profit)}</td>
                                        </tr>
                                    )
                                })}
                                {clientRows.length === 0 && (
                                    <tr><td colSpan={span('clients')}><div className="empty">{loading ? 'Loading…' : 'No clients yet.'}</div></td></tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {tab === 'leads' && (
                        <table>
                            {headers('leads')}
                            <tbody>
                                {leadRows.flatMap((p) => p.opportunities.map((o, i) => (
                                    <tr key={o.id} className={rowClass(p.id, i > 0 && 'subrow')} data-person-id={p.id} onClick={() => rowClick(p.id)}>
                                        {selectCell(p, i === 0)}
                                        <td>
                                            {i === 0 ? (
                                                <div className="cust">
                                                    {p.name || 'Unnamed'}
                                                    {p.opportunities.length > 1 && <span className="multi-badge">{p.opportunities.length} open</span>}
                                                </div>
                                            ) : (
                                                <span className="subrow-connector">↳</span>
                                            )}
                                        </td>
                                        {/* Contact details are the person's, not the pursuit's — the
                                            sub-rows continue the row above rather than repeating them. */}
                                        <td className="phone">{i === 0 ? (p.phone_number || '—') : ''}</td>
                                        <td className="email">{i === 0 ? <div className="ellip">{p.email || '—'}</div> : ''}</td>
                                        <td><RepTag owner={o.owner_detail} /></td>
                                        <td>{o.stage_detail ? pill(o.stage_detail.name, STAGE_COLOR[o.stage_detail.name?.toUpperCase()] || '#888') : '—'}</td>
                                        <td>{o.tier ? pill(cap(o.tier), TIER_COLOR[o.tier] || '#888') : '—'}</td>
                                        <td>{cap(o.source) || '—'}</td>
                                        <td>{monthYear(o.created_at)}</td>
                                    </tr>
                                )))}
                                {leadRows.length === 0 && (
                                    <tr><td colSpan={span('leads')}><div className="empty">{loading ? 'Loading…' : 'No active leads.'}</div></td></tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {tab === 'cold' && (
                        <table>
                            {headers('cold')}
                            <tbody>
                                {coldRows.map((p) => (
                                    <tr key={p.id} className={rowClass(p.id)} data-person-id={p.id} onClick={() => rowClick(p.id)}>
                                        {selectCell(p)}
                                        <td>
                                            <div className="cust">{p.name || 'Unnamed'}</div>
                                            <div className="veh">{p.phone_number || '—'}</div>
                                        </td>
                                        <td><RepTag owner={p.owner_detail} /></td>
                                        <td>{monthYear(p.created_at)}</td>
                                        <td className="r">
                                            {!selecting && (
                                                <button
                                                    className="btn-make-lead"
                                                    onClick={(e) => { e.stopPropagation(); promote(p.id, search) }}
                                                >
                                                    <AddIcon style={{ fontSize: 13 }} /> Make Lead
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {coldRows.length === 0 && (
                                    <tr><td colSpan={span('cold')}><div className="empty">{loading ? 'Loading…' : 'No contacts.'}</div></td></tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {tab === 'households' && (
                        <table>
                            {headers('households')}
                            <tbody>
                                {householdRows.map((h) => (
                                    <tr key={h.id} data-household-id={h.id} onClick={() => openHousehold(h.id)}>
                                        <td>
                                            <div className="cust">{h.name}</div>
                                            <div className="veh">{firstNames(h) || 'No members yet'}</div>
                                        </td>
                                        <td className="r">{h.member_count}</td>
                                    </tr>
                                ))}
                                {householdRows.length === 0 && (
                                    <tr><td colSpan={span('households')}><div className="empty">{loading ? 'Loading…' : 'No households yet.'}</div></td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
                {pager}
                </>
                )}
            </div>
            {isMobile && bulkBar}

            <HouseholdModal
                open={creatingHousehold}
                onClose={() => setCreatingHousehold(false)}
                onDone={onHouseholdDone}
            />

            {confirmAction && (
                <ConfirmDialog
                    open
                    title={confirmAction.title}
                    message={confirmAction.message}
                    confirmLabel={confirmAction.confirmLabel}
                    onCancel={() => setConfirmAction(null)}
                    onConfirm={runConfirmed}
                />
            )}
        </Box>
    )
}
